import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CwConnection,
  CwConnectionTestResult,
  CwCustomerListResult,
  CwCustomerTypeAliasConfig,
  CwAgreementStatusAliasConfig,
  CwAgreementSubtypeAliasConfig,
  CwDataset,
  CwDatasetMappingConfig,
  CwDatasetSampleResult,
  CwDatasetState,
  CwSequentialScheduleState,
  CwImportAllInput,
  CwTestSyncByInvoiceNumberInput,
  CwProjectStatusAliasConfig,
  CwProjectTypeAliasConfig,
  CwOpportunityStageAliasConfig,
  CwOpportunityStatusAliasConfig,
  CwOpportunityTypeAliasConfig,
  CwOpportunitySalesRepAliasConfig,
  CwServiceCategoryAliasConfig,
  CwProductClassAliasConfig,
  CwSourceFieldsResult,
  CwSyncActivityEntry,
  CwSyncActivityFilters,
  CwSyncError,
  CwSyncInput,
  CwSyncSummary,
  CwTestSyncInput,
  CwTestSyncByIdInput,
  CwTicketStatusAliasConfig,
  CwTicketTypeAliasConfig,
  CwTimeEntry,
  CwTimesheetStatusAliasConfig,
  CwWorkRoleAliasConfig,
  CwWorkTypeServiceCategoryAliasConfig,
  CwWorkTypeBillingClassAliasConfig,
  CwChargeCodeAliasConfig,
  PaginatedResult,
  SetCwCustomerFilterInput,
  SetCwCustomerTypeAliasesInput,
  SetCwFieldMappingsInput,
  SetCwProjectStatusAliasesInput,
  SetCwProjectTypeAliasesInput,
  SetCwOpportunityStageAliasesInput,
  SetCwOpportunityStatusAliasesInput,
  SetCwOpportunityTypeAliasesInput,
  SetCwOpportunitySalesRepAliasesInput,
  SetCwAgreementStatusAliasesInput,
  SetCwAgreementSubtypeAliasesInput,
  SetCwServiceCategoryAliasesInput,
  SetCwProductClassAliasesInput,
  SetCwTicketStatusAliasesInput,
  SetCwTicketTypeAliasesInput,
  SetCwTimesheetStatusAliasesInput,
  SetCwWorkRoleAliasesInput,
  SetCwWorkTypeServiceCategoryAliasesInput,
  SetCwWorkTypeBillingClassAliasesInput,
  SetCwChargeCodeAliasesInput,
  SortDirection,
  UpdateCwConnectionInput,
  UpdateCwDatasetInput,
  UpdateCwSequentialScheduleInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface CwTimeEntryListParams {
  page: number;
  pageSize: number;
  companyIdentifier?: string;
  billable?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const connectwiseKeys = {
  all: ['connectwise'] as const,
  connection: ['connectwise', 'connection'] as const,
  customerOptions: (search: string) => ['connectwise', 'customer-options', search] as const,
  timeEntries: (params: CwTimeEntryListParams) => ['connectwise', 'time-entries', params] as const,
  datasets: ['connectwise', 'datasets'] as const,
  sequentialSchedule: ['connectwise', 'sequential-schedule'] as const,
  mappings: (dataset: CwDataset) => ['connectwise', 'datasets', dataset, 'mappings'] as const,
  sourceFields: (dataset: CwDataset) =>
    ['connectwise', 'datasets', dataset, 'source-fields'] as const,
  typeAliases: ['connectwise', 'datasets', 'customers', 'type-aliases'] as const,
  serviceCategoryAliases: [
    'connectwise',
    'datasets',
    'products',
    'service-category-aliases',
  ] as const,
  productClassAliases: ['connectwise', 'datasets', 'products', 'class-aliases'] as const,
  projectTypeAliases: ['connectwise', 'datasets', 'projects', 'project-type-aliases'] as const,
  projectStatusAliases: ['connectwise', 'datasets', 'projects', 'project-status-aliases'] as const,
  opportunityStageAliases: ['connectwise', 'datasets', 'opportunities', 'stage-aliases'] as const,
  opportunityStatusAliases: ['connectwise', 'datasets', 'opportunities', 'status-aliases'] as const,
  opportunityTypeAliases: ['connectwise', 'datasets', 'opportunities', 'type-aliases'] as const,
  opportunitySalesRepAliases: [
    'connectwise',
    'datasets',
    'opportunities',
    'sales-rep-aliases',
  ] as const,
  agreementSubtypeAliases: ['connectwise', 'datasets', 'agreements', 'subtype-aliases'] as const,
  agreementStatusAliases: ['connectwise', 'datasets', 'agreements', 'status-aliases'] as const,
  workRoleAliases: ['connectwise', 'datasets', 'agreements', 'work-role-aliases'] as const,
  ticketStatusAliases: ['connectwise', 'datasets', 'tickets', 'status-aliases'] as const,
  projectTicketStatusAliases: [
    'connectwise',
    'datasets',
    'tickets',
    'project-status-aliases',
  ] as const,
  ticketTypeAliases: ['connectwise', 'datasets', 'tickets', 'type-aliases'] as const,
  workTypeServiceCategoryAliases: [
    'connectwise',
    'datasets',
    'timeEntries',
    'service-category-aliases',
  ] as const,
  workTypeBillingClassAliases: [
    'connectwise',
    'datasets',
    'timeEntries',
    'billing-class-aliases',
  ] as const,
  chargeCodeAliases: ['connectwise', 'datasets', 'timeEntries', 'charge-code-aliases'] as const,
  timesheetStatusAliases: ['connectwise', 'datasets', 'timeEntries', 'status-aliases'] as const,
  sample: (
    dataset: CwDataset,
    limit: number,
    projectId?: string,
    agreementId?: string,
    customerId?: string,
  ) =>
    [
      'connectwise',
      'datasets',
      dataset,
      'sample',
      limit,
      projectId ?? null,
      agreementId ?? null,
      customerId ?? null,
    ] as const,
  syncErrors: ['connectwise', 'sync-errors'] as const,
  syncActivity: (filters: CwSyncActivityFilters) =>
    ['connectwise', 'sync-activity', filters] as const,
};

export function useCwConnection(): UseQueryResult<CwConnection> {
  return useQuery({
    queryKey: connectwiseKeys.connection,
    queryFn: () => api.get<CwConnection>('/connectwise/connection'),
  });
}

/** Live list of ConnectWise companies for the global sync-scope filter picker. */
export function useCwCustomerOptions(
  search: string,
  enabled: boolean,
): UseQueryResult<CwCustomerListResult> {
  return useQuery({
    queryKey: connectwiseKeys.customerOptions(search),
    queryFn: () =>
      api.get<CwCustomerListResult>(
        `/connectwise/connection/customer-options${buildQuery({ search: search || undefined })}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function useSetCwCustomerFilter(): UseMutationResult<
  CwConnection,
  unknown,
  SetCwCustomerFilterInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwCustomerFilterInput) =>
      api.put<CwConnection>('/connectwise/connection/customer-filter', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.connection });
    },
  });
}

export function useCwTimeEntries(
  params: CwTimeEntryListParams,
): UseQueryResult<PaginatedResult<CwTimeEntry>> {
  return useQuery({
    queryKey: connectwiseKeys.timeEntries(params),
    queryFn: () =>
      api.get<PaginatedResult<CwTimeEntry>>(
        `/connectwise/time-entries${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          companyIdentifier: params.companyIdentifier,
          billable: params.billable,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useRunCwSync(): UseMutationResult<CwSyncSummary, unknown, CwSyncInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CwSyncInput) => api.post<CwSyncSummary>('/connectwise/sync', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.all });
    },
  });
}

export function useUpdateCwConnection(): UseMutationResult<
  CwConnection,
  unknown,
  UpdateCwConnectionInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCwConnectionInput) =>
      api.put<CwConnection>('/connectwise/connection', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.connection });
    },
  });
}

export function useTestCwConnection(): UseMutationResult<CwConnectionTestResult, unknown, void> {
  return useMutation({
    mutationFn: () => api.post<CwConnectionTestResult>('/connectwise/connection/test', {}),
  });
}

export function useCwDatasets(): UseQueryResult<CwDatasetState[]> {
  return useQuery({
    queryKey: connectwiseKeys.datasets,
    queryFn: () => api.get<CwDatasetState[]>('/connectwise/datasets'),
    // Poll while a sync runs in the background so the card shows live progress.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.lastOutcome === 'running') ? 4000 : false,
  });
}

export function useCwSequentialSchedule(): UseQueryResult<CwSequentialScheduleState> {
  return useQuery({
    queryKey: connectwiseKeys.sequentialSchedule,
    queryFn: () => api.get<CwSequentialScheduleState>('/connectwise/sequential-schedule'),
    // Poll while the sequence runs so the card shows which dataset is syncing.
    refetchInterval: (query) => (query.state.data?.running ? 4000 : false),
  });
}

export function useUpdateCwSequentialSchedule(): UseMutationResult<
  CwSequentialScheduleState,
  unknown,
  UpdateCwSequentialScheduleInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCwSequentialScheduleInput) =>
      api.put<CwSequentialScheduleState>('/connectwise/sequential-schedule', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.sequentialSchedule });
    },
  });
}

export function useRunCwSequentialSchedule(): UseMutationResult<
  CwSequentialScheduleState,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<CwSequentialScheduleState>('/connectwise/sequential-schedule/run', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.sequentialSchedule });
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
    },
  });
}

export function useCwDatasetMappings(
  dataset: CwDataset,
  enabled: boolean,
): UseQueryResult<CwDatasetMappingConfig> {
  return useQuery({
    queryKey: connectwiseKeys.mappings(dataset),
    queryFn: () => api.get<CwDatasetMappingConfig>(`/connectwise/datasets/${dataset}/mappings`),
    enabled,
  });
}

export function useCwSourceFields(
  dataset: CwDataset,
  enabled: boolean,
): UseQueryResult<CwSourceFieldsResult> {
  return useQuery({
    queryKey: connectwiseKeys.sourceFields(dataset),
    queryFn: () => api.get<CwSourceFieldsResult>(`/connectwise/datasets/${dataset}/source-fields`),
    enabled,
    staleTime: 60_000,
  });
}

export function useCwCustomerTypeAliases(
  enabled: boolean,
): UseQueryResult<CwCustomerTypeAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.typeAliases,
    queryFn: () =>
      api.get<CwCustomerTypeAliasConfig>('/connectwise/datasets/customers/type-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwCustomerTypeAliases(): UseMutationResult<
  CwCustomerTypeAliasConfig,
  unknown,
  SetCwCustomerTypeAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwCustomerTypeAliasesInput) =>
      api.put<CwCustomerTypeAliasConfig>('/connectwise/datasets/customers/type-aliases', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.typeAliases });
    },
  });
}

export function useCwServiceCategoryAliases(
  enabled: boolean,
): UseQueryResult<CwServiceCategoryAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.serviceCategoryAliases,
    queryFn: () =>
      api.get<CwServiceCategoryAliasConfig>(
        '/connectwise/datasets/products/service-category-aliases',
      ),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwServiceCategoryAliases(): UseMutationResult<
  CwServiceCategoryAliasConfig,
  unknown,
  SetCwServiceCategoryAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwServiceCategoryAliasesInput) =>
      api.put<CwServiceCategoryAliasConfig>(
        '/connectwise/datasets/products/service-category-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.serviceCategoryAliases });
    },
  });
}

export function useCwProductClassAliases(
  enabled: boolean,
): UseQueryResult<CwProductClassAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.productClassAliases,
    queryFn: () =>
      api.get<CwProductClassAliasConfig>('/connectwise/datasets/products/class-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwProductClassAliases(): UseMutationResult<
  CwProductClassAliasConfig,
  unknown,
  SetCwProductClassAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwProductClassAliasesInput) =>
      api.put<CwProductClassAliasConfig>('/connectwise/datasets/products/class-aliases', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.productClassAliases });
    },
  });
}

export function useCwProjectTypeAliases(
  enabled: boolean,
): UseQueryResult<CwProjectTypeAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.projectTypeAliases,
    queryFn: () =>
      api.get<CwProjectTypeAliasConfig>('/connectwise/datasets/projects/project-type-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwProjectTypeAliases(): UseMutationResult<
  CwProjectTypeAliasConfig,
  unknown,
  SetCwProjectTypeAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwProjectTypeAliasesInput) =>
      api.put<CwProjectTypeAliasConfig>(
        '/connectwise/datasets/projects/project-type-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.projectTypeAliases });
    },
  });
}

export function useCwAgreementSubtypeAliases(
  enabled: boolean,
): UseQueryResult<CwAgreementSubtypeAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.agreementSubtypeAliases,
    queryFn: () =>
      api.get<CwAgreementSubtypeAliasConfig>('/connectwise/datasets/agreements/subtype-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwAgreementSubtypeAliases(): UseMutationResult<
  CwAgreementSubtypeAliasConfig,
  unknown,
  SetCwAgreementSubtypeAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwAgreementSubtypeAliasesInput) =>
      api.put<CwAgreementSubtypeAliasConfig>(
        '/connectwise/datasets/agreements/subtype-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.agreementSubtypeAliases });
    },
  });
}

export function useCwAgreementStatusAliases(
  enabled: boolean,
): UseQueryResult<CwAgreementStatusAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.agreementStatusAliases,
    queryFn: () =>
      api.get<CwAgreementStatusAliasConfig>('/connectwise/datasets/agreements/status-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwAgreementStatusAliases(): UseMutationResult<
  CwAgreementStatusAliasConfig,
  unknown,
  SetCwAgreementStatusAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwAgreementStatusAliasesInput) =>
      api.put<CwAgreementStatusAliasConfig>(
        '/connectwise/datasets/agreements/status-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.agreementStatusAliases });
    },
  });
}

export function useCwWorkRoleAliases(enabled: boolean): UseQueryResult<CwWorkRoleAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.workRoleAliases,
    queryFn: () =>
      api.get<CwWorkRoleAliasConfig>('/connectwise/datasets/agreements/work-role-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwWorkRoleAliases(): UseMutationResult<
  CwWorkRoleAliasConfig,
  unknown,
  SetCwWorkRoleAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwWorkRoleAliasesInput) =>
      api.put<CwWorkRoleAliasConfig>('/connectwise/datasets/agreements/work-role-aliases', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.workRoleAliases });
    },
  });
}

export function useCwTicketStatusAliases(
  enabled: boolean,
): UseQueryResult<CwTicketStatusAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.ticketStatusAliases,
    queryFn: () =>
      api.get<CwTicketStatusAliasConfig>('/connectwise/datasets/tickets/status-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwTicketStatusAliases(): UseMutationResult<
  CwTicketStatusAliasConfig,
  unknown,
  SetCwTicketStatusAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwTicketStatusAliasesInput) =>
      api.put<CwTicketStatusAliasConfig>('/connectwise/datasets/tickets/status-aliases', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.ticketStatusAliases });
    },
  });
}

export function useCwProjectTicketStatusAliases(
  enabled: boolean,
): UseQueryResult<CwTicketStatusAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.projectTicketStatusAliases,
    queryFn: () =>
      api.get<CwTicketStatusAliasConfig>('/connectwise/datasets/tickets/project-status-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwProjectTicketStatusAliases(): UseMutationResult<
  CwTicketStatusAliasConfig,
  unknown,
  SetCwTicketStatusAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwTicketStatusAliasesInput) =>
      api.put<CwTicketStatusAliasConfig>(
        '/connectwise/datasets/tickets/project-status-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectwiseKeys.projectTicketStatusAliases,
      });
    },
  });
}

export function useCwTicketTypeAliases(enabled: boolean): UseQueryResult<CwTicketTypeAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.ticketTypeAliases,
    queryFn: () => api.get<CwTicketTypeAliasConfig>('/connectwise/datasets/tickets/type-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwTicketTypeAliases(): UseMutationResult<
  CwTicketTypeAliasConfig,
  unknown,
  SetCwTicketTypeAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwTicketTypeAliasesInput) =>
      api.put<CwTicketTypeAliasConfig>('/connectwise/datasets/tickets/type-aliases', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.ticketTypeAliases });
    },
  });
}

export function useCwWorkTypeServiceCategoryAliases(
  enabled: boolean,
): UseQueryResult<CwWorkTypeServiceCategoryAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.workTypeServiceCategoryAliases,
    queryFn: () =>
      api.get<CwWorkTypeServiceCategoryAliasConfig>(
        '/connectwise/datasets/timeEntries/service-category-aliases',
      ),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwWorkTypeServiceCategoryAliases(): UseMutationResult<
  CwWorkTypeServiceCategoryAliasConfig,
  unknown,
  SetCwWorkTypeServiceCategoryAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwWorkTypeServiceCategoryAliasesInput) =>
      api.put<CwWorkTypeServiceCategoryAliasConfig>(
        '/connectwise/datasets/timeEntries/service-category-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectwiseKeys.workTypeServiceCategoryAliases,
      });
    },
  });
}

export function useCwWorkTypeBillingClassAliases(
  enabled: boolean,
): UseQueryResult<CwWorkTypeBillingClassAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.workTypeBillingClassAliases,
    queryFn: () =>
      api.get<CwWorkTypeBillingClassAliasConfig>(
        '/connectwise/datasets/timeEntries/billing-class-aliases',
      ),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwWorkTypeBillingClassAliases(): UseMutationResult<
  CwWorkTypeBillingClassAliasConfig,
  unknown,
  SetCwWorkTypeBillingClassAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwWorkTypeBillingClassAliasesInput) =>
      api.put<CwWorkTypeBillingClassAliasConfig>(
        '/connectwise/datasets/timeEntries/billing-class-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectwiseKeys.workTypeBillingClassAliases,
      });
    },
  });
}

export function useCwChargeCodeAliases(enabled: boolean): UseQueryResult<CwChargeCodeAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.chargeCodeAliases,
    queryFn: () =>
      api.get<CwChargeCodeAliasConfig>('/connectwise/datasets/timeEntries/charge-code-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwChargeCodeAliases(): UseMutationResult<
  CwChargeCodeAliasConfig,
  unknown,
  SetCwChargeCodeAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwChargeCodeAliasesInput) =>
      api.put<CwChargeCodeAliasConfig>(
        '/connectwise/datasets/timeEntries/charge-code-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectwiseKeys.chargeCodeAliases,
      });
    },
  });
}

export function useCwTimesheetStatusAliases(
  enabled: boolean,
): UseQueryResult<CwTimesheetStatusAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.timesheetStatusAliases,
    queryFn: () =>
      api.get<CwTimesheetStatusAliasConfig>('/connectwise/datasets/timeEntries/status-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwTimesheetStatusAliases(): UseMutationResult<
  CwTimesheetStatusAliasConfig,
  unknown,
  SetCwTimesheetStatusAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwTimesheetStatusAliasesInput) =>
      api.put<CwTimesheetStatusAliasConfig>(
        '/connectwise/datasets/timeEntries/status-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.timesheetStatusAliases });
    },
  });
}

export function useCwProjectStatusAliases(
  enabled: boolean,
): UseQueryResult<CwProjectStatusAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.projectStatusAliases,
    queryFn: () =>
      api.get<CwProjectStatusAliasConfig>('/connectwise/datasets/projects/project-status-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwProjectStatusAliases(): UseMutationResult<
  CwProjectStatusAliasConfig,
  unknown,
  SetCwProjectStatusAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwProjectStatusAliasesInput) =>
      api.put<CwProjectStatusAliasConfig>(
        '/connectwise/datasets/projects/project-status-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.projectStatusAliases });
    },
  });
}

export function useCwOpportunityStageAliases(
  enabled: boolean,
): UseQueryResult<CwOpportunityStageAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.opportunityStageAliases,
    queryFn: () =>
      api.get<CwOpportunityStageAliasConfig>('/connectwise/datasets/opportunities/stage-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwOpportunityStageAliases(): UseMutationResult<
  CwOpportunityStageAliasConfig,
  unknown,
  SetCwOpportunityStageAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwOpportunityStageAliasesInput) =>
      api.put<CwOpportunityStageAliasConfig>(
        '/connectwise/datasets/opportunities/stage-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.opportunityStageAliases });
    },
  });
}

export function useCwOpportunityStatusAliases(
  enabled: boolean,
): UseQueryResult<CwOpportunityStatusAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.opportunityStatusAliases,
    queryFn: () =>
      api.get<CwOpportunityStatusAliasConfig>('/connectwise/datasets/opportunities/status-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwOpportunityStatusAliases(): UseMutationResult<
  CwOpportunityStatusAliasConfig,
  unknown,
  SetCwOpportunityStatusAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwOpportunityStatusAliasesInput) =>
      api.put<CwOpportunityStatusAliasConfig>(
        '/connectwise/datasets/opportunities/status-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.opportunityStatusAliases });
    },
  });
}

export function useCwOpportunityTypeAliases(
  enabled: boolean,
): UseQueryResult<CwOpportunityTypeAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.opportunityTypeAliases,
    queryFn: () =>
      api.get<CwOpportunityTypeAliasConfig>('/connectwise/datasets/opportunities/type-aliases'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwOpportunityTypeAliases(): UseMutationResult<
  CwOpportunityTypeAliasConfig,
  unknown,
  SetCwOpportunityTypeAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwOpportunityTypeAliasesInput) =>
      api.put<CwOpportunityTypeAliasConfig>(
        '/connectwise/datasets/opportunities/type-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.opportunityTypeAliases });
    },
  });
}

export function useCwOpportunitySalesRepAliases(
  enabled: boolean,
): UseQueryResult<CwOpportunitySalesRepAliasConfig> {
  return useQuery({
    queryKey: connectwiseKeys.opportunitySalesRepAliases,
    queryFn: () =>
      api.get<CwOpportunitySalesRepAliasConfig>(
        '/connectwise/datasets/opportunities/sales-rep-aliases',
      ),
    enabled,
    staleTime: 60_000,
  });
}

export function useSetCwOpportunitySalesRepAliases(): UseMutationResult<
  CwOpportunitySalesRepAliasConfig,
  unknown,
  SetCwOpportunitySalesRepAliasesInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetCwOpportunitySalesRepAliasesInput) =>
      api.put<CwOpportunitySalesRepAliasConfig>(
        '/connectwise/datasets/opportunities/sales-rep-aliases',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: connectwiseKeys.opportunitySalesRepAliases,
      });
    },
  });
}

export function useUpdateCwDataset(): UseMutationResult<
  CwDatasetState,
  unknown,
  { dataset: CwDataset; input: UpdateCwDatasetInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, input }) =>
      api.patch<CwDatasetState>(`/connectwise/datasets/${dataset}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
    },
  });
}

export function useSetCwDatasetMappings(): UseMutationResult<
  CwDatasetMappingConfig,
  unknown,
  { dataset: CwDataset; input: SetCwFieldMappingsInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, input }) =>
      api.put<CwDatasetMappingConfig>(`/connectwise/datasets/${dataset}/mappings`, input),
    onSuccess: (_data, { dataset }) => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.mappings(dataset) });
    },
  });
}

export function useSyncCwDataset(): UseMutationResult<
  CwSyncSummary,
  unknown,
  { dataset: CwDataset; input: CwSyncInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, input }) =>
      api.post<CwSyncSummary>(`/connectwise/datasets/${dataset}/sync`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.syncErrors });
    },
  });
}

export function useCwDatasetSample(
  dataset: CwDataset,
  limit: number,
  enabled: boolean,
  filter?: { projectId?: string; agreementId?: string; customerId?: string },
): UseQueryResult<CwDatasetSampleResult> {
  const projectId = filter?.projectId;
  const agreementId = filter?.agreementId;
  const customerId = filter?.customerId;
  return useQuery({
    queryKey: connectwiseKeys.sample(dataset, limit, projectId, agreementId, customerId),
    queryFn: () =>
      api.get<CwDatasetSampleResult>(
        `/connectwise/datasets/${dataset}/sample${buildQuery({ limit, projectId, agreementId, customerId })}`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

export function useTestSyncCwDataset(): UseMutationResult<
  CwSyncSummary,
  unknown,
  { dataset: CwDataset; input: CwTestSyncInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, input }) =>
      api.post<CwSyncSummary>(`/connectwise/datasets/${dataset}/test`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
    },
  });
}

export function useTestSyncByIdCwDataset(): UseMutationResult<
  CwSyncSummary,
  unknown,
  { dataset: CwDataset; input: CwTestSyncByIdInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, input }) =>
      api.post<CwSyncSummary>(`/connectwise/datasets/${dataset}/test-by-id`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
    },
  });
}

export function useImportAllCwDataset(): UseMutationResult<
  CwSyncSummary,
  unknown,
  { dataset: CwDataset; input: CwImportAllInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dataset, input }) =>
      api.post<CwSyncSummary>(`/connectwise/datasets/${dataset}/import-all`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
    },
  });
}

export function useTestSyncByInvoiceNumberCwDataset(): UseMutationResult<
  CwSyncSummary,
  unknown,
  CwTestSyncByInvoiceNumberInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post<CwSyncSummary>(`/connectwise/datasets/invoices/test-by-number`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.datasets });
    },
  });
}

export function useCwSyncErrors(): UseQueryResult<CwSyncError[]> {
  return useQuery({
    queryKey: connectwiseKeys.syncErrors,
    queryFn: () => api.get<CwSyncError[]>('/connectwise/sync-errors'),
  });
}

export function useClearCwSyncErrors(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>('/connectwise/sync-errors'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: connectwiseKeys.syncErrors });
    },
  });
}

export function useCwSyncActivity(
  filters: CwSyncActivityFilters,
): UseQueryResult<CwSyncActivityEntry[]> {
  return useQuery({
    queryKey: connectwiseKeys.syncActivity(filters),
    queryFn: () =>
      api.get<CwSyncActivityEntry[]>(`/connectwise/sync-activity${buildQuery({ ...filters })}`),
  });
}
